/**
 * Data layer for `igaming-player-ltv-per-affiliate`. Thin typed fetch wrappers over /api/v1/igaming-player-ltv-per-affiliate.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface IgamingPlayerLtvPerAffiliateRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListIgamingPlayerLtvPerAffiliateResponse {
	count: number;
	items: IgamingPlayerLtvPerAffiliateRow[];
}

export interface CreateIgamingPlayerLtvPerAffiliateBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/igaming-player-ltv-per-affiliate";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListIgamingPlayerLtvPerAffiliateResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListIgamingPlayerLtvPerAffiliateResponse>(res);
}

export async function create(body: CreateIgamingPlayerLtvPerAffiliateBody): Promise<IgamingPlayerLtvPerAffiliateRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<IgamingPlayerLtvPerAffiliateRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const igamingPlayerLtvPerAffiliateQueryKeys = {
	all: ["igaming-player-ltv-per-affiliate"] as const,
	list: () => [...igamingPlayerLtvPerAffiliateQueryKeys.all] as const,
	detail: (id: string) => [...igamingPlayerLtvPerAffiliateQueryKeys.all, id] as const,
};
